// Admin panel: single master login, then a moderation list of all events
// (including soft-removed) with restore, soft-remove, hard-delete, view, and
// edit. Authority comes from the signed httpOnly session cookie.

import {
  adminListEvents,
  adminLogin,
  adminLogout,
  adminSession,
  adminSetStatus,
  ApiError,
  deleteEvent,
  getEventDetail,
  updateEvent,
} from '../lib/api';
import { formatDateTime } from '../lib/format';
import { navigate } from '../lib/router';
import { renderEventForm } from './eventForm';
import type { LocalEventDetail } from '../types/event';

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
}

export function renderAdminPage(root: HTMLElement): void {
  root.innerHTML = `<section class="page"><div class="placeholder">Checking admin session...</div></section>`;
  // Quiet probe (always 200) so the console stays clean when logged out.
  adminSession()
    .then((authed) => {
      if (!authed) {
        renderLogin(root, '');
        return;
      }
      adminListEvents()
        .then((data) => renderList(root, data.events))
        .catch((err) => {
          if (err instanceof ApiError && (err.status === 401 || err.status === 503)) {
            renderLogin(root, err.status === 503 ? 'Admin is not configured on the server.' : '');
          } else {
            root.innerHTML = `<section class="page"><div class="placeholder error">Could not load admin.</div></section>`;
          }
        });
    });
}

function renderLogin(root: HTMLElement, notice: string): void {
  root.innerHTML = `
    <section class="page admin-login">
      <a class="back-link" href="/" data-link>&larr; Back to events</a>
      <h2>Admin login</h2>
      ${notice ? `<div class="banner warn">${esc(notice)}</div>` : ''}
      <form id="login-form" class="event-form">
        <input type="text" name="username" value="admin" autocomplete="username"
               class="visually-hidden" aria-hidden="true" tabindex="-1" readonly />
        <label class="field">
          <span class="field-label">Password</span>
          <input name="password" type="password" autocomplete="current-password" required />
        </label>
        <p class="form-error" id="login-error" role="alert"></p>
        <button class="btn primary" type="submit">Log in</button>
      </form>
    </section>
  `;
  const form = root.querySelector<HTMLFormElement>('#login-form')!;
  const err = root.querySelector<HTMLElement>('#login-error')!;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    err.textContent = '';
    const pw = form.querySelector<HTMLInputElement>('[name=password]')!.value;
    const btn = form.querySelector<HTMLButtonElement>('button')!;
    btn.disabled = true;
    try {
      await adminLogin(pw);
      renderAdminPage(root);
    } catch (e2) {
      btn.disabled = false;
      err.textContent = e2 instanceof ApiError ? e2.message : 'Login failed.';
    }
  });
}

function renderList(root: HTMLElement, events: LocalEventDetail[]): void {
  root.innerHTML = `
    <section class="page admin-panel">
      <div class="admin-head">
        <a class="back-link" href="/" data-link>&larr; Back to events</a>
        <button id="logout-btn" class="btn small" type="button">Log out</button>
      </div>
      <h2>Moderation</h2>
      <p class="page-lead">${events.length} event${events.length === 1 ? '' : 's'} total (local, user-created).</p>
      <div class="admin-list" id="admin-list"></div>
    </section>
  `;

  root.querySelector<HTMLButtonElement>('#logout-btn')!.addEventListener('click', async () => {
    await adminLogout();
    navigate('/admin');
    renderAdminPage(root);
  });

  const list = root.querySelector<HTMLElement>('#admin-list')!;
  if (events.length === 0) {
    list.innerHTML = '<div class="placeholder">No user-created events yet.</div>';
    return;
  }

  for (const ev of events) {
    const id = ev.sourceId || ev.id;
    const row = document.createElement('div');
    row.className = 'admin-row' + (ev.status === 'removed' ? ' removed' : '');
    row.innerHTML = `
      <div class="admin-row-main">
        <strong>${esc(ev.title)}</strong>
        ${ev.age21Plus ? '<span class="badge-21 small">21+</span>' : ''}
        ${ev.status === 'removed' ? '<span class="status-pill">removed</span>' : ''}
        <div class="admin-row-meta">${esc(formatDateTime(ev.start))} · ${esc(ev.category)} · ${esc(ev.address || '')}</div>
      </div>
      <div class="admin-row-actions"></div>
    `;
    const actions = row.querySelector<HTMLElement>('.admin-row-actions')!;

    const view = document.createElement('a');
    view.className = 'btn small ghost';
    view.textContent = 'View';
    view.href = `/e/${id}`;
    view.addEventListener('click', (e) => {
      e.preventDefault();
      navigate(`/e/${id}`);
    });
    actions.appendChild(view);

    const edit = document.createElement('button');
    edit.className = 'btn small';
    edit.type = 'button';
    edit.textContent = 'Edit';
    edit.addEventListener('click', () => navigate(`/admin/edit/${id}`));
    actions.appendChild(edit);

    if (ev.status === 'removed') {
      const restore = document.createElement('button');
      restore.className = 'btn small';
      restore.type = 'button';
      restore.textContent = 'Restore';
      restore.addEventListener('click', async () => {
        restore.disabled = true;
        try {
          await adminSetStatus(id, 'published');
          renderAdminPage(root);
        } catch {
          restore.disabled = false;
        }
      });
      actions.appendChild(restore);
    } else {
      const remove = document.createElement('button');
      remove.className = 'btn small';
      remove.type = 'button';
      remove.textContent = 'Hide';
      remove.addEventListener('click', async () => {
        remove.disabled = true;
        try {
          await adminSetStatus(id, 'removed');
          renderAdminPage(root);
        } catch {
          remove.disabled = false;
        }
      });
      actions.appendChild(remove);
    }

    const del = document.createElement('button');
    del.className = 'btn small danger';
    del.type = 'button';
    del.textContent = 'Delete';
    del.addEventListener('click', async () => {
      if (!confirm(`Permanently delete "${ev.title}" and its files?`)) return;
      del.disabled = true;
      try {
        await deleteEvent(id, {});
        renderAdminPage(root);
      } catch {
        del.disabled = false;
      }
    });
    actions.appendChild(del);

    list.appendChild(row);
  }
}

// Admin edit reuses the event form, authorized by the session cookie (no token).
export function renderAdminEditPage(root: HTMLElement, params: Record<string, string>): void {
  const id = params.id;
  root.innerHTML = `<section class="page"><div class="placeholder">Loading...</div></section>`;
  void (async () => {
    try {
      const ev = await getEventDetail(id);
      root.innerHTML = `
        <section class="page manage-page">
          <a class="back-link" href="/admin" data-link>&larr; Back to admin</a>
          <h2>Edit event (admin)</h2>
          <div id="form-mount"></div>
          <p class="form-error" id="admin-edit-error" role="alert"></p>
        </section>`;
      const form = renderEventForm(
        {
          name: ev.title,
          category: ev.category,
          age21Plus: ev.age21Plus,
          description: ev.description,
          address: ev.address,
          locationName: ev.venueName,
          start: ev.start,
          end: ev.end,
        },
        { submitLabel: 'Save changes' }
      );
      root.querySelector<HTMLElement>('#form-mount')!.appendChild(form.element);
      const err = root.querySelector<HTMLElement>('#admin-edit-error')!;
      form.element.addEventListener('submit', async (e) => {
        e.preventDefault();
        const { value, errors } = form.read();
        if (!value) {
          form.showErrors(errors);
          return;
        }
        form.setBusy(true);
        try {
          await updateEvent(id, value, {});
          navigate('/admin');
        } catch (e2) {
          form.setBusy(false);
          if (e2 instanceof ApiError && e2.fields) form.showErrors(e2.fields);
          err.textContent = e2 instanceof ApiError ? e2.message : 'Could not save.';
        }
      });
    } catch {
      root.innerHTML = `<section class="page"><div class="placeholder error">Could not load this event (admin session required).</div></section>`;
    }
  })();
}
